import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest, BaseResponse } from '../models'
import { AccountQuota } from '../../models/AccountQuota'
import { RetentionPolicy, RetentionResource, RetentionRule } from '../../models/RetentionPolicy'
import { removeEmptyItems } from '../../common/util'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Account, type ComplianceFileType, type ComplianceType } from '../../models/Account'
import { accountUserSettingsApi } from '../accountUserSettings/accountUserSettings'

const _BASE_URL = import.meta.env.REACT_APP_ACCOUNTS_API_BASE_URL!

export interface RetentionPolicyRequest extends BaseRequest {
  policyId: string
}

export interface CreateOrUpdateRetentionPolicyBody {
  rule?: RetentionRule
  resource?: RetentionResource
  enabled?: boolean
}

export interface UpdateRetentionPolicyRequest extends RetentionPolicyRequest {
  body: CreateOrUpdateRetentionPolicyBody
}

export interface CreateRetentionPolicyRequest extends BaseRequest {
  body: CreateOrUpdateRetentionPolicyBody
}

export interface UpdatePhoneComplianceNoticeConfigBody {
  manualAudioEnabled?: boolean
  periodicBeepEnabled?: boolean
}

export interface UpdateComplianceNoticeConfigBody {
  imageEnabled?: boolean
  audioEnabled?: boolean
  inPersonAudioEnabled?: boolean
  chatEnabled?: boolean
  chatMessage?: string
  phone?: UpdatePhoneComplianceNoticeConfigBody
}

export interface UpdateComplianceNoticeFileBody {
  fileType: ComplianceFileType
  fileId: string
  type: ComplianceType
}

export interface ResetComplianceNoticeFileBody {
  fileType: ComplianceFileType
  type: ComplianceType
}

export interface UpdateLogoFileBody {
  fileId: string
}

export interface UpdateComplianceNoticeConfigRequest extends BaseRequest {
  body: UpdateComplianceNoticeConfigBody
}

export interface UpdateComplianceNoticeFileRequest extends BaseRequest {
  body: UpdateComplianceNoticeFileBody
}

export interface ResetComplianceNoticeFileRequest extends BaseRequest {
  body: ResetComplianceNoticeFileBody
}

export interface UpdateLogoFileRequest extends BaseRequest {
  body: UpdateLogoFileBody
}

export type ResetLogoFileRequest = BaseRequest

const refreshIamAccountOnSuccess = async (
  _: unknown,
  { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled
    dispatch(accountUserSettingsApi.util.invalidateTags(['Account']))
  } catch {
    return
  }
}

export const accountApi = createApi({
  reducerPath: 'accountApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['RetentionPolicies', 'Account'],
  endpoints: (builder) => ({
    getResolvedQuotas: builder.query<BaseResponse<AccountQuota>, BaseRequest>({
      query: (request) => {
        return {
          url: `/account-quota/${request.accountId}`,
        }
      },
    }),
    getRetentionPolicies: builder.query<BaseResponse<RetentionPolicy[]>, BaseRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/retention-policy`,
        }
      },
      providesTags: ['RetentionPolicies'],
    }),
    getRetentionPolicy: builder.query<BaseResponse<RetentionPolicy>, RetentionPolicyRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/retention-policy/${request.policyId}`,
        }
      },
    }),
    createRetentionPolicy: builder.mutation<void, CreateRetentionPolicyRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/retention-policy`,
          body: removeEmptyItems(request.body),
          method: 'POST',
        }
      },
      invalidatesTags: ['RetentionPolicies'],
    }),
    updateRetentionPolicy: builder.mutation<void, UpdateRetentionPolicyRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/retention-policy/${request.policyId}`,
          body: request.body,
          method: 'PATCH',
        }
      },
      invalidatesTags: ['RetentionPolicies'],
    }),
    deleteRetentionPolicy: builder.mutation<void, RetentionPolicyRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/retention-policy/${request.policyId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['RetentionPolicies'],
    }),
    updateComplianceConfig: builder.mutation<Account, UpdateComplianceNoticeConfigRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/compliance-notice/config`,
          body: request.body,
          method: 'PATCH',
        }
      },
    }),
    updateComplianceFile: builder.mutation<Account, UpdateComplianceNoticeFileRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/compliance-notice/file`,
          body: request.body,
          method: 'PATCH',
        }
      },
    }),
    resetComplianceFile: builder.mutation<Account, ResetComplianceNoticeFileRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/compliance-notice/reset-file`,
          body: request.body,
          method: 'PATCH',
        }
      },
    }),
    updateLogoFile: builder.mutation<Account, UpdateLogoFileRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/assets/logo`,
          body: request.body,
          method: 'PUT',
        }
      },
      transformResponse(response: BaseResponse<Account>) {
        return response.data
      },
      invalidatesTags: ['Account'],
      onQueryStarted: refreshIamAccountOnSuccess,
    }),
    resetLogoFile: builder.mutation<Account, ResetLogoFileRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/assets/logo`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['Account'],
      onQueryStarted: refreshIamAccountOnSuccess,
    }),
  }),
})

export const {
  useGetResolvedQuotasQuery,
  useGetRetentionPoliciesQuery,
  useGetRetentionPolicyQuery,
  useLazyGetRetentionPolicyQuery,
  useUpdateRetentionPolicyMutation,
  useDeleteRetentionPolicyMutation,
  useCreateRetentionPolicyMutation,
  useUpdateComplianceConfigMutation,
  useUpdateComplianceFileMutation,
  useResetComplianceFileMutation,
  useUpdateLogoFileMutation,
  useResetLogoFileMutation,
} = accountApi
